import Mustache from 'mustache';
import { debounce } from './helpers';

/**
 * @typedef {Object} FilteringOptions
 * @property {string} ajaxUrl - Base URL for AJAX requests
 * @property {string} templateId - ID of the Mustache template
 * @property {string} [moreBtnId] - ID of the load more button
 * @property {string} [totalResultsId] - ID of the total results container
 * @property {number} [debounceTime=250] - Debounce time in milliseconds
 */

/**
 * @typedef {Object} FilteringState
 * @property {boolean} isProcessing
 * @property {string} nextPageUrl
 * @property {number} totalItems
 * @property {Error|null} error
 */

/**
 * @typedef {HTMLFormElement & {
 *   dataset: {
 *     ajaxUrl: string,
 *     targetContainer: string,
 *     template: string,
 *     moreBtn?: string,
 *     totalResults?: string
 *   }
 * }} FormElement
 */

/**
 * Content filtering module for handling form submissions and updates.
 */
class ContentFiltering {
  /** @type {FilteringState} */
  #state = {
    isProcessing: false,
    nextPageUrl: '',
    totalItems: 0,
    error: null
  };

  /** Counter used to generate unique ids for the sr-only filter instructions. */
  static #instanceCount = 0;

  /**
   * Creates an instance of ContentFiltering.
   * @param {HTMLFormElement} formEl - The form element to attach filtering to.
   * @param {Partial<FilteringOptions>} [options={}] - Optional configuration.
   */
  constructor(formEl, options = {}) {
    this.validateInitialization(formEl);

    // Core elements
    this.form = formEl;

    this.ajaxUrl = options.ajaxUrl || formEl.dataset.ajaxUrl;
    this.resultContainer = document.querySelector('.article-grid');
    this.spinnerContainer = document.querySelector('.spinner');

    // Template setup
    const templateId = options.templateId || formEl.dataset.template;
    const templateElement = document.getElementById(templateId);
    if (!templateElement) {
      throw new Error(`Template element with ID "${templateId}" not found`);
    }
    this.template = templateElement.innerHTML;

    // Optional elements
    this.moreBtn = options.moreBtnId
      ? document.getElementById(options.moreBtnId)
      : (formEl.dataset.moreBtn ? document.getElementById(formEl.dataset.moreBtn) : null);

    // Initialize nextPageUrl from moreBtn if available
    if (this.moreBtn && this.moreBtn.dataset.ajaxUrl) {
      this.#state.nextPageUrl = this.moreBtn.dataset.ajaxUrl;
    }

    this.totalResultsContainer = options.totalResultsId ?
      document.getElementById(options.totalResultsId) :
      (formEl.dataset.totalResults ? document.getElementById(formEl.dataset.totalResults) : null);

    // Store bound handlers so they can be removed in destroy()
    this._boundChange = this.handleChange.bind(this);
    this._boundSubmit = this.handleSubmit.bind(this);
    this._boundLoadMore = this.handleLoadMore.bind(this);

    this.setupAccessibility();
    this.setupResetButton();
    this.setupEventListeners();

    this.debouncedTrigger = debounce(
      this.triggerSubmit.bind(this),
      options.debounceTime || 250
    );
  }

  /**
   * Validates required elements and throws descriptive errors if missing.
   * @private
   * @param {HTMLFormElement} formEl - The form element to validate.
   * @throws {Error} If required attributes or elements are missing.
   */
  validateInitialization(formEl) {
    if (!(formEl instanceof HTMLFormElement)) {
      throw new Error('ContentFiltering: Form element required');
    }

    const required = ['ajaxUrl', 'targetContainer', 'template'];
    const missing = required.filter(attr => !formEl.dataset[attr]);

    if (missing.length > 0) {
      throw new Error(`ContentFiltering: Required data attributes missing: ${missing.join(', ')}`);
    }

    if (!document.querySelector('.article-grid')) {
      //do not initialize if target container is not found
      return;
    }
  }

  /**
   * Applies the ARIA attributes and screen-reader-only helpers needed so
   * assistive technology is informed about loading state, result counts,
   * errors, and the fact that filters submit automatically on change.
   * @private
   * @returns {void}
   */
  setupAccessibility() {
    if (this.spinnerContainer) {
      this.spinnerContainer.setAttribute('role', 'status');
      this.spinnerContainer.setAttribute('aria-live', 'polite');
      this.spinnerContainer.setAttribute('aria-atomic', 'true');
    }

    if (this.totalResultsContainer) {
      this.totalResultsContainer.setAttribute('role', 'status');
      this.totalResultsContainer.setAttribute('aria-live', 'polite');
      this.totalResultsContainer.setAttribute('aria-atomic', 'true');
    }

    if (this.resultContainer) {
      this.resultContainer.setAttribute('aria-busy', 'false');
    }

    this.form.setAttribute('aria-busy', 'false');

    // Filters submit automatically on change (no explicit submit button),
    // which is a WCAG 3.2.2 "On Input" context change. Tell AT users so
    // the automatic update isn't disorienting. Guarded against re-running
    // setupAccessibility on a form that was already initialized (e.g. a
    // re-init without an intervening destroy()), which would otherwise
    // stack up duplicate sr-only paragraphs and aria-describedby ids.
    if (this.form.dataset.filterInstructionsId) {
      this.instructionsEl = document.getElementById(this.form.dataset.filterInstructionsId);
      return;
    }

    const instructionsId = `content-filtering-instructions-${ContentFiltering.#instanceCount++}`;
    const instructions = document.createElement('p');
    instructions.id = instructionsId;
    instructions.className = 'sr-only';
    instructions.textContent = this.form.dataset.filterInstructions
      || 'Tulokset päivittyvät automaattisesti, kun muutat suodattimia.';
    this.form.insertAdjacentElement('beforebegin', instructions);
    this.instructionsEl = instructions;
    this.form.dataset.filterInstructionsId = instructionsId;

    const describedBy = [this.form.getAttribute('aria-describedby'), instructionsId]
      .filter(Boolean)
      .join(' ');
    this.form.setAttribute('aria-describedby', describedBy);
  }

  /**
   * Shows or hides the loading spinner, including a screen-reader-only
   * status text since the spinner graphic itself has no accessible name.
   * @private
   * @param {boolean} isLoading - Whether the spinner should be shown.
   * @returns {void}
   */
  setSpinnerLoading(isLoading) {
    if (!this.spinnerContainer) return;

    this.spinnerContainer.innerHTML = '';
    if (!isLoading) return;

    const indicator = document.createElement('div');
    indicator.className = 'loading-indicator';
    indicator.setAttribute('aria-hidden', 'true');
    indicator.innerHTML = '<div class="loading-spinner"></div>';

    const srText = document.createElement('span');
    srText.className = 'sr-only';
    srText.textContent = this.spinnerContainer.dataset.loadingText || 'Ladataan tuloksia…';

    this.spinnerContainer.append(indicator, srText);
  }

  /**
   * Sets up event listeners for the form.
   * @private
   * @returns {void}
   */
  setupEventListeners() {
    this.form.addEventListener('change', this._boundChange);
    this.form.addEventListener('submit', this._boundSubmit);

    if (this.moreBtn) {
      this.moreBtn.addEventListener('click', this._boundLoadMore);
    }
  }

  /**
   * Sets up reset functionality.
   * @private
   * @returns {void}
   */
  setupResetButton() {

    //reset button exist in form already
    const resetBtn = this.form.querySelector('button[type="reset"]');
    if (resetBtn) {
      this.resetBtn = resetBtn;

      resetBtn.addEventListener('click', () => {
        this.form.reset();
        this.triggerSubmit();
      });

    }
  }

  /**
   * Handles form changes.
   * @private
   * @param {Event} event - The event object.
   * @returns {void}
   */
  handleChange(event) {
    if (
      this.#state.isProcessing ||
      !(
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLSelectElement ||
        event.target instanceof HTMLTextAreaElement
      )
    ) return;

    this.resetBtn?.classList.toggle('hidden', !this.hasActiveFilters());

    if (event.target.type === 'date') {
      this.debouncedTrigger();
    } else {
      this.triggerSubmit();
    }
  }

  /**
   * Checks if any filters are currently active.
   * @private
   * @returns {boolean} True if any filters are active, false otherwise.
   */
  hasActiveFilters() {
    const formData = new FormData(this.form);

    for (const [, value] of formData.entries()) {
      if (value && value !== '') return true;
    }
    return false;
  }

  /**
   * Handles the load more button click.
   * @private
   * @returns {Promise<void>}
   */
  async handleLoadMore() {
    if (this.#state.isProcessing || !this.#state.nextPageUrl) return;

    // Store the current number of items to identify new ones
    const buttonText = this.moreBtn.innerHTML;
    const originalAriaLabel = this.moreBtn.getAttribute('aria-label');
    const loadingText = this.moreBtn.dataset.loadingText || 'Ladataan lisää tuloksia…';

    // Show loading indicator in the more button and disable it. The visual
    // markup replaces the button's text, so aria-label preserves an
    // accessible name for screen readers while it's loading.
    this.moreBtn.disabled = true;
    this.moreBtn.setAttribute('aria-busy', 'true');
    this.moreBtn.setAttribute('aria-label', loadingText);
    this.moreBtn.innerHTML = '<div class="loading-indicator" aria-hidden="true"><div class="loading-spinner"></div></div>';

    try {
      const currentItemCount = this.resultContainer.children.length;

      const response = await this.fetchData(this.#state.nextPageUrl);

      this.#state.nextPageUrl = response.nextPage;
      this.appendResults(response);

      // Focus on the first new item if it was a keyboard interaction
      this.focusFirstNewItem(currentItemCount);
    } catch (error) {
      this.handleError(error);
    } finally {
      this.moreBtn.disabled = false;
      this.moreBtn.innerHTML = buttonText;
      if (originalAriaLabel !== null) {
        this.moreBtn.setAttribute('aria-label', originalAriaLabel);
      } else {
        this.moreBtn.removeAttribute('aria-label');
      }
      this.moreBtn.setAttribute('aria-busy', 'false');
    }
  }

  /**
   * Focuses on the first new item after loading more content.
   * @private
   * @param {number} previousItemCount - The number of items before loading.
   * @returns {void}
   */
  focusFirstNewItem(previousItemCount) {
    const allItems = this.resultContainer.children;
    if (allItems.length > previousItemCount) {
      const firstNewItem = allItems[previousItemCount];

      // Find the first focusable element within the new item
      const focusableElement = firstNewItem.querySelector(
        'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElement) {
        focusableElement.focus();
      } else {
        // If no focusable element found, make the item itself temporarily focusable
        firstNewItem.setAttribute('tabindex', '0');
        firstNewItem.focus();
        firstNewItem.addEventListener('blur', () => firstNewItem.removeAttribute('tabindex'), { once: true });
      }
    }
  }

  /**
   * Handles form submission.
   * @private
   * @param {Event} event - The event object.
   * @returns {Promise<void>}
   */
  async handleSubmit(event) {
    event.preventDefault();
    if (this.#state.isProcessing) return;

    try {
      this.setLoadingState(true);
      const formData = this.getFormData();

      const query = this.serialize(formData);
      const url = this.buildUrl(query);

      console.log('[content-filtering] fetch url:', url);

      const response = await this.fetchData(url);

      console.log('[content-filtering] server response:', response);

      this.updateResults(response);
    } catch (error) {
      this.handleError(error);
    } finally {
      this.setLoadingState(false);
    }
  }

  /**
   * Fetches data from the server.
   * @private
   * @param {string} url - The URL to fetch data from.
   * @returns {Promise<Object>} The response data.
   * @throws {Error} If the response is not ok.
   */
  async fetchData(url) {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  /**
   * Updates the UI with new results.
   * @private
   * @param {Object} data - The data to render.
   * @returns {void}
   */
  updateResults(data) {
    this.spinnerContainer.innerHTML = '';
    this.resultContainer.innerHTML = this.renderTemplate(data.items);
    this.updateTotalResults(data.total || 0);
    this.#state.nextPageUrl = data.nextPage || '';
    this.#state.totalItems = data.total || 0;
    // Don't call updateMoreButton here - it will be handled by setLoadingState
  }

  /**
   * Appends more results to existing ones.
   * @private
   * @param {Object} data - The data to append.
   * @returns {void}
   */
  appendResults(data) {
    const markup = this.renderTemplate(data.items);
    this.resultContainer.insertAdjacentHTML('beforeend', markup);

    this.updateMoreButton(data.nextPage);
    this.#state.nextPageUrl = data.nextPage || '';
  }

  /**
   * Updates the more button visibility.
   * @private
   * @param {string} [nextPage] - The URL for the next page, if any.
   * @returns {void}
   */
  updateMoreButton(nextPage) {
    if (!this.moreBtn) return;

    if (nextPage) {
      this.moreBtn.classList.remove('hidden');
      this.moreBtn.dataset.ajaxUrl = nextPage;
    } else {
      this.moreBtn.classList.add('hidden');
    }
  }

  /**
   * Handles errors during data fetching.
   * @private
   * @param {Error} error - The error object.
   * @returns {void}
   */
  handleError(error) {
    console.error('Content filtering error:', error);
    this.#state.error = error;
    this.updateTotalResults(0);

    // Show error message to user. role="alert" ensures it's announced to
    // screen readers even though nothing moved focus there.
    const errorText = this.resultContainer.dataset.errorText
      || 'Tulosten lataamisessa tapahtui virhe. Yritä myöhemmin uudelleen.';

    this.resultContainer.innerHTML = '';
    const errorEl = document.createElement('div');
    errorEl.className = 'error-message';
    errorEl.setAttribute('role', 'alert');
    errorEl.textContent = errorText;
    this.resultContainer.appendChild(errorEl);
  }

  /**
   * Sets loading state and updates UI accordingly.
   * @private
   * @param {boolean} isLoading - Whether loading is in progress.
   * @returns {void}
   */
  setLoadingState(isLoading) {
    this.#state.isProcessing = isLoading;
    this.form.classList.toggle('processing', isLoading);
    this.form.setAttribute('aria-busy', String(isLoading));
    this.resultContainer?.setAttribute('aria-busy', String(isLoading));

    // Hide more button during loading
    if (this.moreBtn) {
      if (isLoading) {
        this.moreBtn.classList.add('hidden');
      } else {
        // Only show button if there's a next page and no error
        if (this.#state.nextPageUrl && !this.#state.error) {
          this.moreBtn.classList.remove('hidden');
        }
      }
    }

    // Update loading UI
    this.setSpinnerLoading(isLoading);
    if (isLoading) {
      this.resultContainer.innerHTML = '';
    }

    // Disable/enable form controls
    Array.from(this.form.elements).forEach(element => {
      if (element instanceof HTMLInputElement) {
        element.disabled = isLoading;
      }
    });
  }

  /**
   * Collects form data as an object, including all checked checkboxes (even if none are checked).
   * @private
   * @returns {Object} - The form data.
   */
  getFormData() {
    const formData = new FormData(this.form);
    const result = {};

    // Collect all values from FormData (works for most fields)
    for (const [key, value] of formData.entries()) {

      if (key.endsWith('[]')) {
        const fieldName = key.replace('[]', '');
        if (!result[fieldName]) result[fieldName] = [];
        result[fieldName].push(value.toString());
      } else {
        result[key] = value.toString();
      }
    }

    //get free text input value
    const freeTextInput = this.form.querySelector('input[name="q"]');

    if (freeTextInput) {
      result.q = freeTextInput.value;
    }


    // Explicitly collect hidden input values to ensure they're included
    const hiddenInputs = this.form.querySelectorAll('input[type="hidden"]');
    hiddenInputs.forEach(input => {
      if (input.name && input.value !== undefined) {
        if (input.name.endsWith('[]')) {
          const fieldName = input.name.replace('[]', '');
          if (!result[fieldName]) result[fieldName] = [];
          result[fieldName].push(input.value);
        } else {
          result[input.name] = input.value;
        }
      }
    });

    // Ensure all checkbox groups are present, even if none are checked
    const checkboxes = this.form.querySelectorAll('input[type="checkbox"][name="types[]"]');
    /* get all checked checkboxes values */
    const checkedCheckboxes = Array.from(checkboxes).filter(cb => cb.checked);
    if (checkedCheckboxes.length > 0) {
      result.types = checkedCheckboxes.map(cb => cb.value);
    }

    // Handle tags specifically - ensure they're always arrays
    if (result.tags && !Array.isArray(result.tags)) {
      result.tags = [result.tags];
    }

    return result;
  }

  /**
   * Serializes form data into a query string.
   * @private
   * @param {Object} data - The data to serialize.
   * @returns {string} - The query string.
   */
  serialize(data) {
    return Object.entries(data).map(([key, value]) => {
      if (Array.isArray(value)) {
        return value.map(v => `${encodeURIComponent(key)}[]=${encodeURIComponent(v)}`).join('&');
      }
      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    }).join('&');
  }

  /**
   * Triggers a form submit event.
   * @returns {void}
   */
  triggerSubmit() {
    // console.log('triggerSubmit');
    this.form.dispatchEvent(new Event('submit', {bubbles: false, cancelable: true}));
  }

  /**
   * Updates the total results display.
   * @param {number} [total=0] - The total number of results.
   * @returns {void}
   */
  updateTotalResults(total = 0) {
    if (!this.totalResultsContainer) return;

    const mode = total === 1 ? 'single' : 'plural';

    const selectedOptions = Array.from(this.form.elements)
      .filter(el => el instanceof HTMLSelectElement && el.name === 'themes[]')
      .map(el => Array.from(el.options).filter(option => option.selected))
      .flat()
      .map(option => option.innerText);

    const typesTxt = selectedOptions.length > 0 ? `${this.totalResultsContainer.dataset.types} ${selectedOptions.join(', ')}` : '';
    const resultTxt = `${total} ${this.totalResultsContainer.dataset[mode] || ''} ${typesTxt}`;

    this.totalResultsContainer.innerText = resultTxt;
  }

  /**
   * Renders the Mustache template with items.
   * @private
   * @param {Object[]} items - The items to render.
   * @returns {string} - The rendered HTML.
   */
  renderTemplate(items) {
    return Mustache.render(this.template, {items: items});
  }

  /**
   * Builds the request URL with query parameters.
   * @param {string} query - The query string.
   * @returns {string} The full URL.
   */
  buildUrl(query) {
    const baseUrl = this.ajaxUrl;
    return query ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${query}` : baseUrl;
  }

  /**
   * Cleans up event listeners and references.
   * @public
   * @returns {void}
   */
  destroy() {
    this.form.removeEventListener('change', this._boundChange);
    this.form.removeEventListener('submit', this._boundSubmit);
    if (this.moreBtn) {
      this.moreBtn.removeEventListener('click', this._boundLoadMore);
    }
    if (this.resetBtn) {
      this.resetBtn.remove();
    }
    if (this.instructionsEl) {
      this.instructionsEl.remove();
    }
    delete this.form.dataset.filterInstructionsId;
  }
}

/**
 * Initializes content filtering on all forms with the .js--content-filtering class.
 * @param {Object} [options] - Optional configuration.
 * @returns {void}
 */
export default {
  init(options = {}) {
    document.querySelectorAll('.js--content-filtering').forEach(form => {
      if (form instanceof HTMLFormElement) {
        new ContentFiltering(form, options);
      }
    });
  }
};
